import styled from "styled-components";
import PropTypes from "prop-types"; // Importing PropTypes for type checking
import { Box, Flex } from "./layout";

// Helper function to resolve responsive values
const resolveResponsiveValue = (value, defaultValue) =>
  typeof value === "object"
    ? {
        xs: value.xs || defaultValue,
        sm: value.sm || value.xs || defaultValue,
        md: value.md || value.sm || value.xs || defaultValue,
        lg: value.lg || value.md || value.sm || value.xs || defaultValue,
        xl:
          value.xl ||
          value.lg ||
          value.md ||
          value.sm ||
          value.xs ||
          defaultValue,
        xxl:
          value.xxl ||
          value.xl ||
          value.lg ||
          value.md ||
          value.sm ||
          value.xs ||
          defaultValue,
      }
    : {
        xs: value || defaultValue,
        sm: value || defaultValue,
        md: value || defaultValue,
        lg: value || defaultValue,
        xl: value || defaultValue,
        xxl: value || defaultValue,
      };

// Calculate width value for grid
const calculateWidth = (value) =>
  value === "auto" ? "auto" : value ? `${(value / 12) * 100}%` : "100%";

// Automatically calculate flex based on the width value
const calculateFlex = (value) =>
  value === "auto" || value ? "0 0 auto" : "1 0 0%";

// Row component with responsive spacing
export const Row = styled(Flex)((props) => {
  const { specing, rowGap, alignItem,justifyContent } = props;

  // Default spacing value
  const defaultSpecing = specing || 20;
  const xsSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.xs || defaultSpecing
      : defaultSpecing;
  const smSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.sm || xsSpecing
      : xsSpecing;
  const mdSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.md || smSpecing
      : xsSpecing;
  const lgSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.lg || mdSpecing
      : mdSpecing;
  const xlSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.xl || lgSpecing
      : lgSpecing;
  const xxlSpecing =
    typeof defaultSpecing === "object"
      ? defaultSpecing.xxl || xlSpecing
      : xlSpecing; // New xxl specing

  return {
    flexWrap: "wrap",
    marginInline: `-${xxlSpecing / 2 / 16}rem`,
    "--specing": `${xxlSpecing / 16}rem`,
    "@media (min-width: 1201px) and (max-width: 1500px)": {
      // New xxl media query
      marginInline: `-${xlSpecing / 2 / 16}rem`,
      "--specing": `${xlSpecing / 16}rem`,
    },
    "@media (min-width: 1001px) and (max-width: 1200px)": {
      marginInline: `-${lgSpecing / 2 / 16}rem`,
      "--specing": `${lgSpecing / 16}rem`,
    },
    "@media (min-width: 701px) and (max-width: 1000px)": {
      marginInline: `-${mdSpecing / 2 / 16}rem`,
      "--specing": `${mdSpecing / 16}rem`,
    },
    "@media (min-width: 501px) and (max-width: 700px)": {
      marginInline: `-${smSpecing / 2 / 16}rem`,
      "--specing": `${smSpecing / 16}rem`,
    },
    "@media (max-width: 500px)": {
      marginInline: `-${xsSpecing / 2 / 16}rem`,
      "--specing": `${xsSpecing / 16}rem`,
    },
    width: "calc(100% + var(--specing))",
    rowGap: rowGap ? "var(--specing)" : 0,
    alignItem: alignItem,
    justifyContent: justifyContent || "flex-start",
  };
});

// Add defaultProps and propTypes to Row
Row.defaultProps = {
  specing: 20,
};

Row.propTypes = {
  specing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
      xxl: PropTypes.number, // New xxl prop type
    }),
  ]),
};

// Col component with automatic width and flex behavior
export const Col = styled(Box)((props) => {
  const { xs, sm, md, lg, xl, xxl } = props;

  // Resolve responsive widths, carrying values upwards if only sm is provided
  const resolvedCols = resolveResponsiveValue({ xs, sm, md, lg, xl, xxl });

  const resolvedWidth = {
    xs: calculateWidth(resolvedCols.xs),
    sm: calculateWidth(resolvedCols.sm),
    md: calculateWidth(resolvedCols.md),
    lg: calculateWidth(resolvedCols.lg),
    xl: calculateWidth(resolvedCols.xl),
    xxl: calculateWidth(resolvedCols.xxl), // New xxl width
  };

  // Automatically calculate flex based on the width
  const resolvedFlex = {
    xs: calculateFlex(resolvedCols.xs),
    sm: calculateFlex(resolvedCols.sm),
    md: calculateFlex(resolvedCols.md),
    lg: calculateFlex(resolvedCols.lg),
    xl: calculateFlex(resolvedCols.xl),
    xxl: calculateFlex(resolvedCols.xxl), // New xxl flex
  };

  return {
    width: resolvedWidth.xxl, // Use xxl width
    maxWidth: resolvedWidth.xxl,
    flex: resolvedFlex.xxl, // Use xxl flex
    paddingInline: "calc(var(--specing) / 2)",
    "@media (min-width: 1201px) and (max-width: 1500px)": {
      // New xxl media query
      width: resolvedWidth.xl,
      maxWidth: resolvedWidth.xl,
      flex: resolvedFlex.xl,
    },
    "@media (min-width: 1001px) and (max-width: 1200px)": {
      width: resolvedWidth.lg,
      maxWidth: resolvedWidth.lg,
      flex: resolvedFlex.lg,
    },
    "@media (min-width: 701px) and (max-width: 1000px)": {
      width: resolvedWidth.md,
      maxWidth: resolvedWidth.md,
      flex: resolvedFlex.md,
    },
    "@media (min-width: 501px) and (max-width: 700px)": {
      width: resolvedWidth.sm,
      maxWidth: resolvedWidth.sm,
      flex: resolvedFlex.sm,
    },
    "@media (max-width: 500px)": {
      width: resolvedWidth.xs,
      maxWidth: resolvedWidth.xs,
      flex: resolvedFlex.xs,
    },
  };
});

// Add defaultProps and propTypes to Col
Col.defaultProps = {
  xs: undefined,
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined,
  xxl: undefined, // New xxl default prop
};

Col.propTypes = {
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]),
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]),
  xxl: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["auto"])]), // New xxl prop type
};
